import { useMutation, UseMutationOptions, UseMutationResult, useQuery, useQueryClient, UseQueryOptions, UseQueryResult } from "@tanstack/react-query";
import { useState } from "react";
import { API_ENDPOINTS } from "../../../constants/api_endpoints";
import { QData } from "../../../types/basic-type-list";
import { TResponse, TResponseWithQData } from "../../../types/common";
import { IDeleteDataResponseAndPayload } from "../../../types/api/author";
import { IAuthorPayload } from "../../../types/api/author";
import { authorDetail, deleteAutor, listAuthor, saveAuthor } from "./callback";

export const useAuthorSave = (
  { qData = { page: 1, limit: 20 }, onSuccess, onError }: {
    qData?: QData;
    onSuccess?: (res: TResponse<IAuthorPayload>) => void;
    onError?: (error: unknown) => void;
  }
  ,
  options: UseMutationOptions<TResponse<IAuthorPayload>, unknown, IAuthorPayload> = {}
): UseMutationResult<TResponse<IAuthorPayload>, unknown, IAuthorPayload> => {
  const queryClient = useQueryClient();

  return useMutation<TResponse<IAuthorPayload>, unknown, IAuthorPayload>({
    mutationFn: async (data: IAuthorPayload) => {
      const response = await saveAuthor(data);
      return response.data;
    },
    onSuccess: (res: any, ...rest) => {
      queryClient.setQueryData([API_ENDPOINTS.AUTHOR_SAVE, { ...qData, total: null }], (old: any) => {
        if (!old) return;
        const exists = old.data.find((v: any) => v._id === res.data._id);
        return {
          ...old,
          data: exists  
            ? old.data.map((v: any) => (v._id === res.data._id ? res.data : v))
            : [res.data, ...old.data]
        };
      });

      onSuccess?.(res);
      options?.onSuccess?.(res, ...rest);
    },
    onError: (error, ...rest) => {
      onError?.(error);
      options?.onError?.(error, ...rest);
    },
    ...options
  });
};

export const useAuthorList = (
  qData: QData,
  options?: Omit<
    UseQueryOptions<TResponseWithQData<IAuthorPayload[]>, Error>,
    'queryKey' | 'queryFn'
  >
): UseQueryResult<TResponseWithQData<IAuthorPayload[]>, Error> & {
  refetchWithQuery: (params: Partial<QData>) => void;
} => {
  const [queryParams, setQueryParams] = useState<QData>({ ...qData });

  const queryResult = useQuery<TResponseWithQData<IAuthorPayload[]>, Error>({
    queryKey: [API_ENDPOINTS.AUTHOR_LIST, { ...queryParams, total: null }],
    queryFn: async () => {
      const res = await listAuthor(queryParams);
      return res.data;
    },
    select: (res) => ({
      ...res,
      data: res.data.map(x => ({ key: x._id, ...x })),
      qData: { ...queryParams, total: res.extra?.total }
    }),
    initialData: {
      data: [],
      success: true,
      message: '',
      code: 200,
      qData: { page: 1, limit: 20 },
      extra: { page: 1, limit: 20, total: 0 },
    },
    staleTime: 300000,
    initialDataUpdatedAt: 0,
    refetchOnWindowFocus: false,
    ...options,
  });

  return {
    ...queryResult,
    refetchWithQuery: (params: Partial<QData>) => {
      setQueryParams((prev: any) => ({ ...prev, ...params }));
    },
  };
};

export const useAuthorDelete = (
  { qData = { page: 1, limit: 20 }, onSuccess, onError }: {
    qData?: QData;
    onSuccess?: (res: TResponse<IDeleteDataResponseAndPayload>) => void;
    onError?: (error: unknown) => void;
  },
  options: UseMutationOptions<
    TResponse<IDeleteDataResponseAndPayload>,
    unknown,
    IDeleteDataResponseAndPayload
  > = {}
) => {

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: IDeleteDataResponseAndPayload) => {
      const response = await deleteAutor(data);
      return response.data;
    },

    onSuccess: (res, variables, ...rest) => {
      const deleteId = variables.ids;

      queryClient.setQueryData(
        [API_ENDPOINTS.AUTHOR_LIST, { ...qData, total: null }],
        (old: any) => {
          if (!old) return old;

          return {
            ...old,
            data: old.data.filter((item: any) => item._id !== deleteId) 
          };
        }
      );

      onSuccess?.(res);
      options?.onSuccess?.(res, variables, ...rest);
    },

    onError: (error, variables, ...rest) => {
      onError?.(error);
      options?.onError?.(error, variables, ...rest);
    },

    ...options
  });
};


export const useAuthorDetails = (
  _id: string,
  options?: Omit<
    UseQueryOptions<TResponse<IAuthorPayload>, Error>,
    "queryKey" | "queryFn"
  >
): UseQueryResult<TResponse<IAuthorPayload>, Error> => {
  return useQuery<TResponse<IAuthorPayload>, Error>({
    queryKey: [API_ENDPOINTS.AUTHOR_DETAILS, _id],
    queryFn: async () => {
      const res = await authorDetail(_id);
      return res.data;
    },
    enabled: !!_id,
    refetchOnWindowFocus: false,
    staleTime: 300000,
    ...options,
  });
};

